/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeftLong } from "react-icons/fa6";
import { TfiInfoAlt } from "react-icons/tfi";
import OTPInput from "react-otp-input";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { useFontStyle } from "~/hook/useFontStyle";
import { getFirebaseMessaging } from "~/lib/firebase/firebase.client";
import { getLocalStorage, setLocalStorage } from "~/lib/helperFuncation/localStorageService";
import { resendOtpTimer } from "~/lib/helperFuncation/resendOtpTimer";
import { getSession, removeSession } from "~/lib/helperFuncation/sessionStorageService";
import {
  loginWithFirebaseVerifyToken,
  resendForgotPasswordOtpThunk,
  resendOtpThunk,
  setError,
  setErrorNull,
  setIsDisabledNextButton,
  setLoginStep,
  verifyForgotPasswordOtpThunk,
  verifyOtpThunk,
} from "~/lib/store/slice/loginSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { AUTH_COOKIE_TOKEN, ICON_ORANGE_COLOR, RECAPTCHA_TOKEN, TIMER_KEY, USER_COUNTRY_PHONE_CODE, USER_MOBILE_NUMBER } from "~/utils/constant";
import { setCookie } from "~/utils/cookies";
import { getExpiryFromToken } from "~/utils/jwt";
import { useFirebaseOTPSender, useFirebaseOTPVerifier } from "./FirebaseAuthIntegration";
import { ComponentHeader, LoginFlowButton, LoginResponseErrorComponent } from "./Login";
import getEnv from "~/utils/get-env";
import ReCaptcha from "../shared-components/ReCaptcha";

const TIMER_DURATION = 60;

const VerifyOtpComponent = () => {
  const { normalInter } = useFontStyle();
  const navigate = useNavigate();
  const location = useLocation().pathname;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [captchaTokenForResendAndForgot, setCaptchaTokenForResendAndForgot] = useState<string | null>(null);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const [otp, setOtp] = useState("");

  // Restore timer from localStorage or set to default
  const [resendTimer, setResendTimer] = useState(() => {
    const savedTimer = Number(getLocalStorage(TIMER_KEY)) || TIMER_DURATION;
    return savedTimer;
  });

  const { error, message, isGotoForgotPasswordOtpVeification, useFirebaseAuth, navigatTo, isFirebaseLogin, recaptchaTokenRedux } = useAppSelector(
    (state) => state.login,
  );

  const mobile = getLocalStorage(USER_MOBILE_NUMBER) || "";
  const countryCode = getLocalStorage(USER_COUNTRY_PHONE_CODE) || "";
  const captchaToken = recaptchaTokenRedux || getSession(RECAPTCHA_TOKEN) || null;

  // Firebase OTP sender hook - must be after watch is defined
  const firebaseOTP = useFirebaseOTPSender({
    phoneNumber: mobile || "",
    countryCode: countryCode || 91,
    recaptchaToken: captchaToken,
    onFirebaseSuccess: (msg) => console.log(msg),
    onFirebaseError: (err) => dispatch(setError(err)),
  });

  // const { executeRecaptcha } = useGoogleReCaptcha();

  // Firebase OTP verifier hook
  const firebaseOTPVerifier = useFirebaseOTPVerifier();

  // Handle resend OTP
  const handleResend = async () => {
    if (resendTimer === 0) {
      if (isFirebaseLogin) {
        const isSuccess = await firebaseOTP.resendOTP();
        if (isSuccess) {
          setResendTimer(TIMER_DURATION);
          setLocalStorage(TIMER_KEY, TIMER_DURATION.toString());
          dispatch(setErrorNull());
          setOtp("");
        }
      } else {
        if (!captchaToken) {
          toast.error("Please verify that you are not a robot");
          return;
        }
        isGotoForgotPasswordOtpVeification
          ? handleForgotPasswordResendOtpThunk(captchaTokenForResendAndForgot!)
          : handleResendOtpThunk(captchaTokenForResendAndForgot!);
      }
    }
  };

  const handleForgotPasswordResendOtpThunk = async (token: string) => {
    try {
      await dispatch(resendForgotPasswordOtpThunk({ mobile, country_code: countryCode, recaptchaToken: token })).unwrap();
      setRefreshReCaptcha((prev) => !prev);
      setResendTimer(TIMER_DURATION);
      setLocalStorage(TIMER_KEY, TIMER_DURATION.toString());
      dispatch(setErrorNull());
      setOtp("");
      toast.success("OTP resent successfully!");
    } catch (err: any) {
      const msg = err?.message || "";
      if (msg.includes("30 min")) setResendTimer(1800);
      else if (msg.includes("1 day")) setResendTimer(14400);
    }
  };

  const handleResendOtpThunk = async (token: string) => {
    try {
      await dispatch(resendOtpThunk({ mobile, country_code: countryCode, recaptchaToken: token })).unwrap();
      setRefreshReCaptcha((prev) => !prev);
      setResendTimer(TIMER_DURATION);
      setLocalStorage(TIMER_KEY, TIMER_DURATION.toString());
      dispatch(setErrorNull());
      setOtp("");
      toast.success("OTP resent successfully!");
    } catch (err: any) {
      const msg = err?.message || "";
      if (msg.includes("30 min")) setResendTimer(1800);
      else if (msg.includes("1 day")) setResendTimer(14400);
    }
  };

  const initPush = async () => {
    try {
      const messaging = await getFirebaseMessaging(dispatch);
      if (!messaging) return;
      console.log("Firebase messaging initialized");
    } catch (err) {
      console.error("FCM init error:", err);
    }
  };

  // Handle OTP verification
  const handleLoginClick = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    // Check if using Firebase auth
    // Use Firebase OTP verification
    if (isFirebaseLogin) {
      const fbToken = await firebaseOTPVerifier.verifyOTP(otp);
      if (fbToken) {
        // Read fresh from session — token was saved when OTP was sent
        const recaptchaToken = getSession(RECAPTCHA_TOKEN) || "";
        const payload = { mobile: mobile, recaptchaToken, country_code: countryCode, fbToken };
        const response = await dispatch(loginWithFirebaseVerifyToken(payload)).unwrap();

        if (response.success) {
          const token = response.data.contenttoken;
          const expiry = getExpiryFromToken(token);
          const maxAge = expiry && expiry > Date.now() ? Math.floor((expiry - Date.now()) / 1000) : 60 * 60 * 24;
          setCookie(AUTH_COOKIE_TOKEN, token, {
            path: "/",
            expires: maxAge,
            secure: true,
            sameSite: "strict",
          });

          navigate(navigatTo ?? `${location}`);
          initPush();
          removeSession(RECAPTCHA_TOKEN);
        }
      }
      return;
    }

    // Otherwise use backend OTP verification
    const payload = { mobile: mobile, otp: otp, country_code: countryCode };
    try {
      let response;
      if (isGotoForgotPasswordOtpVeification) {
        response = await dispatch(verifyForgotPasswordOtpThunk(payload)).unwrap();
      } else {
        response = await dispatch(verifyOtpThunk(payload)).unwrap();
      }

      if (response?.contenttoken) {
        const token = response.contenttoken;
        const expiry = getExpiryFromToken(token);
        const maxAge = expiry && expiry > Date.now() ? Math.floor((expiry - Date.now()) / 1000) : 60 * 60 * 24;

        setCookie(AUTH_COOKIE_TOKEN, token, {
          path: "/",
          expires: maxAge,
          secure: true,
          sameSite: "strict",
        });

        navigate(navigatTo ?? `${location}`);
        initPush();
        if (!("Notification" in window)) return;
        // request permission (optional if already done)
        Notification.requestPermission();

        // setLocalStorage(USER_HAS_SMK, response.is_smk);
        // setLocalStorage(USER_ID, response.user_id);
      }
    } catch (err) {
      console.error("OTP verification failed:", err);
      // Timer will continue even if OTP is invalid
    }
  };

  // Timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          const newTime = prev - 1;
          setLocalStorage(TIMER_KEY, newTime.toString());
          return newTime;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  const onVerify = useCallback((captchaToken: string) => {
    setCaptchaTokenForResendAndForgot(captchaToken);
  }, []);

  return (
    <div className="w-full h-dvh md:h-full flex flex-col justify-start md:justify-center items-center gap-6 relative">
      {/* Back button */}
      <div
        className="absolute w-auto -left-2 -top-5 md:-top-10 flex justify-center items-center gap-2 cursor-pointer"
        onClick={() => {
          dispatch(setLoginStep(1));
          dispatch(setIsDisabledNextButton(true));
          dispatch(setErrorNull());
          removeSession(RECAPTCHA_TOKEN);
        }}
      >
        <FaArrowLeftLong size={10} color={ICON_ORANGE_COLOR} className="h-full flex justify-center items-center" />
        <span className="text-14 text-textHeadingColor font-inter font-medium capitalize">{t("login_page.back")}</span>
      </div>

      {/* Header */}

      <ComponentHeader title={t("login_page.enter_otp")} text={t("login_page.otp_description")} />

      {/* Error / Info Message */}
      {error ? (
        <LoginResponseErrorComponent />
      ) : (
        <div className="w-full flex justify-center items-start bg-secondaryBackground rounded-md p-3 gap-2">
          <TfiInfoAlt size={25} color={"#9d715e"} />
          <span className="text-14 text-textSecondaryColor font-inter font-normal text-left">{message}</span>
        </div>
      )}

      {/* OTP Input & Button */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleLoginClick();
        }}
        className="w-full flex flex-col justify-center items-center gap-6"
      >
        {isGotoForgotPasswordOtpVeification ? (
          <ReCaptcha getCaptchaToken={onVerify} action="forgot" refreshReCaptcha={refreshReCaptcha} />
        ) : (
          <ReCaptcha getCaptchaToken={onVerify} action="resend" refreshReCaptcha={refreshReCaptcha} />
        )}
        {/* OTP Input */}
        <div className="w-full">
          <OTPInput
            value={otp || ""}
            onChange={setOtp}
            numInputs={6}
            renderInput={(props) => (
              <input
                {...props}
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                className="!w-[40px] !h-[40px] !md:w-[48px] md:h-[48px] mx-1 rounded-lg bg-transparent border-[1px] border-borderPrimary text-xl text-center outline-none focus:ring-1 focus:ring-primaryColor !text-textColor"
              />
            )}
            containerStyle="flex justify-center gap-2"
          />
        </div>

        {/* Verify + Resend */}
        <div className="w-full flex flex-col justify-center items-center gap-3">
          <LoginFlowButton text={t("login_page.verify_otp")} />

          {/* Resend Link */}
          <div className="w-full flex justify-center items-center gap-2">
            {resendTimer > 0 ? (
              <span className={cn("text-14 text-gray-400 ", normalInter)}>
                {t("login_page.resend_in")} {resendOtpTimer(resendTimer)}
              </span>
            ) : (
              <span className={cn("text-14 text-textColor font-inter font-medium", normalInter)}>
                {t("login_page.otp_note")}{" "}
                <button
                  className="text-textHeadingColor cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResend();
                  }}
                >
                  {t("login_page.resend_again")}
                </button>
              </span>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default VerifyOtpComponent;
